import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onScrollToContent: () => void;
}

const HeroSection = ({ onScrollToContent }: HeroSectionProps) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-conversion rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-accent/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="AcademicCapIcon" size={20} className="text-accent" />
              <span className="text-sm font-medium text-primary-foreground">Educational Resource</span>
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-primary-foreground mb-6 leading-tight">
              Understanding Carbon Credits Made Simple
            </h1>
            
            <p className="text-lg sm:text-xl text-primary-foreground/90 mb-8 leading-relaxed">
              Learn how carbon credits work, why they matter, and how they can transform your business into a sustainability leader. Expert insights from Dr. Nupur.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={onScrollToContent}
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 shadow-natural-lg hover:shadow-natural-xl transition-all duration-300 ease-organic transform hover:scale-102">

                <span>Start Learning</span>
                <Icon name="ArrowDownIcon" size={20} />
              </button>
              
              <a
                href="https://wa.me/1234567890?text=I%20have%20questions%20about%20carbon%20credits"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-primary-foreground text-primary font-heading font-semibold rounded-lg hover:bg-primary-foreground/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic">

                <Icon name="ChatBubbleLeftRightIcon" size={20} />
                <span>Ask Questions</span>
              </a>
            </div>
          </div>
          
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-natural-xl">
              <AppImage
                src="https://images.unsplash.com/photo-1614953167872-5fdffc05440a"
                alt="Lush green forest canopy with sunlight filtering through dense tree leaves showing natural carbon sequestration"
                className="w-full h-[400px] lg:h-[500px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent"></div>
            </div>
            
            <div className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-natural-lg p-6 max-w-xs">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-conversion/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckBadgeIcon" size={24} className="text-conversion" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-text-primary">2.5M+</p>
                  <p className="text-sm text-text-secondary">Tons CO₂ Offset</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;